import { useState } from 'react';
import { http } from '@/lib/api';
import { toast } from 'sonner';
import { Sparkles, CalendarDays, Loader2, X, ChevronDown, ChevronUp } from 'lucide-react';

/**
 * Admin-only: AI-draft a day-by-day Growth Plan (one action per day) in the
 * exact GROWTH_PLANS format, review/edit each day, then publish live.
 */
export default function AIPlanGenerator({ onPublished }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState('form');
  const [busy, setBusy] = useState(false);

  const [topic, setTopic] = useState('');
  const [days, setDays] = useState(7);
  const [premium, setPremium] = useState(false);
  const [notes, setNotes] = useState('');

  const [draft, setDraft] = useState(null); // { plan }
  const [showJson, setShowJson] = useState(false);

  const reset = () => { setStep('form'); setDraft(null); setTopic(''); setNotes(''); setDays(7); setPremium(false); setShowJson(false); };
  const close = () => { if (busy) return; setOpen(false); reset(); };

  const generate = async () => {
    if (!topic.trim()) { toast.error('Enter a topic first'); return; }
    setBusy(true);
    try {
      const { data } = await http.post('/admin/plans/generate',
        { topic: topic.trim(), days: Number(days), premium, notes: notes.trim() },
        { timeout: 180000 });
      setDraft(data.plan);
      setStep('review');
      toast.success('Draft ready — review and publish');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Generation failed. Please try again.');
    } finally { setBusy(false); }
  };

  const publish = async () => {
    if (!draft) return;
    setBusy(true);
    try {
      const { data } = await http.post('/admin/plans/publish', { plan: draft }, { timeout: 60000 });
      toast.success(`Published "${data.plan?.title}" — ${data.plan?.days} days live`);
      setOpen(false); reset();
      onPublished && onPublished();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Publish failed');
    } finally { setBusy(false); }
  };

  const setField = (k, v) => setDraft(d => ({ ...d, [k]: v }));
  const setDay = (i, v) => setDraft(d => ({ ...d, daily: d.daily.map((x, idx) => idx === i ? v : x) }));
  const syncJson = (txt) => { try { setDraft(JSON.parse(txt)); } catch { /* keep typing */ } };

  return (
    <>
      <button
        data-testid="ai-plan-generate-btn"
        onClick={() => setOpen(true)}
        className="text-xs bg-beige text-navy rounded-full px-4 py-2 inline-flex items-center gap-1.5 font-semibold hover:brightness-95 transition border border-navy/10">
        <CalendarDays size={13} /> Generate Plan (AI)
      </button>

      {open && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" onClick={close}>
          <div onClick={e => e.stopPropagation()} data-testid="ai-plan-modal"
            className="w-full max-w-2xl bg-white rounded-3xl p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <p className="font-heading text-xl inline-flex items-center gap-2">
                <CalendarDays size={18} className="text-gold" /> {step === 'form' ? 'Generate a Growth Plan' : 'Review & Publish'}
              </p>
              <button data-testid="ai-plan-close" onClick={close}><X size={18} /></button>
            </div>

            {step === 'form' && (
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-medium text-navy/70">Plan topic</label>
                  <textarea data-testid="ai-plan-topic" value={topic} onChange={e => setTopic(e.target.value)} rows={2}
                    placeholder="e.g. Building a consistent morning routine"
                    className="mt-1 w-full text-sm bg-[#F9F8F6] border border-navy/10 rounded-2xl p-3 focus:outline-none focus:ring-2 focus:ring-gold" />
                </div>
                <div className="flex gap-3">
                  <div className="flex-1">
                    <label className="text-xs font-medium text-navy/70">Length</label>
                    <select data-testid="ai-plan-days" value={days} onChange={e => setDays(e.target.value)}
                      className="mt-1 w-full text-sm bg-[#F9F8F6] border border-navy/10 rounded-2xl p-3 focus:outline-none focus:ring-2 focus:ring-gold">
                      {[7, 14, 30].map(n => <option key={n} value={n}>{n} days</option>)}
                    </select>
                  </div>
                  <div className="flex-1">
                    <label className="text-xs font-medium text-navy/70">Access</label>
                    <button data-testid="ai-plan-premium" onClick={() => setPremium(p => !p)}
                      className={`mt-1 w-full text-sm rounded-2xl p-3 border transition ${premium ? 'bg-navy text-white border-navy' : 'bg-[#F9F8F6] text-navy border-navy/10'}`}>
                      {premium ? 'Premium (members)' : 'Free (everyone)'}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-navy/70">Notes to the AI (optional)</label>
                  <input data-testid="ai-plan-notes" value={notes} onChange={e => setNotes(e.target.value)}
                    placeholder="Tone, audience, must-cover points…"
                    className="mt-1 w-full text-sm bg-[#F9F8F6] border border-navy/10 rounded-2xl p-3 focus:outline-none focus:ring-2 focus:ring-gold" />
                </div>
                <button data-testid="ai-plan-generate-submit" onClick={generate} disabled={busy}
                  className="w-full bg-gold text-navy rounded-full py-3 font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">
                  {busy ? <><Loader2 size={16} className="animate-spin" /> Generating…</> : <><Sparkles size={16} /> Generate</>}
                </button>
                <p className="text-[10px] text-navy/50 text-center">Uses your AI credits. You&apos;ll review everything before it goes live.</p>
              </div>
            )}

            {step === 'review' && draft && (
              <div className="space-y-4">
                <div className="bg-[#F9F8F6] rounded-2xl p-4 space-y-3">
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-navy/50">Plan title</label>
                    <input data-testid="ai-plan-title" value={draft.title || ''} onChange={e => setField('title', e.target.value)}
                      className="mt-1 w-full text-sm font-semibold bg-white border border-navy/10 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gold" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-navy/50">Description</label>
                    <input data-testid="ai-plan-desc" value={draft.description || ''} onChange={e => setField('description', e.target.value)}
                      className="mt-1 w-full text-sm bg-white border border-navy/10 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gold" />
                  </div>
                  <div className="flex items-center gap-3 text-xs text-navy/60">
                    <span>{draft.days} days</span>
                    <button onClick={() => setField('premium', !draft.premium)}
                      className={`rounded-full px-3 py-1.5 border ${draft.premium ? 'bg-navy text-white border-navy' : 'bg-white text-navy border-navy/15'}`}>
                      {draft.premium ? 'Premium' : 'Free'}
                    </button>
                  </div>
                </div>

                <div className="space-y-2" data-testid="ai-plan-days-list">
                  {(draft.daily || []).map((d, i) => (
                    <div key={i} className="flex items-start gap-2 border border-navy/10 rounded-2xl p-2.5">
                      <span className="w-6 h-6 shrink-0 rounded-full bg-gold text-navy text-[11px] font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
                      <textarea value={d} onChange={e => setDay(i, e.target.value)} rows={2}
                        className="flex-1 text-xs bg-transparent focus:outline-none resize-none" />
                    </div>
                  ))}
                </div>

                <div>
                  <button onClick={() => setShowJson(s => !s)} className="text-xs text-navy/60 inline-flex items-center gap-1">
                    {showJson ? <ChevronUp size={12} /> : <ChevronDown size={12} />} Advanced: edit raw JSON
                  </button>
                  {showJson && (
                    <textarea data-testid="ai-plan-json" defaultValue={JSON.stringify(draft, null, 2)} onChange={e => syncJson(e.target.value)} rows={12}
                      className="mt-2 w-full font-mono text-[11px] bg-[#F9F8F6] border border-navy/10 rounded-2xl p-3 focus:outline-none focus:ring-2 focus:ring-gold" />
                  )}
                </div>

                <div className="flex gap-2">
                  <button data-testid="ai-plan-back" onClick={() => setStep('form')} disabled={busy}
                    className="flex-1 bg-beige text-navy rounded-full py-3 font-medium disabled:opacity-60">Back</button>
                  <button data-testid="ai-plan-publish" onClick={publish} disabled={busy}
                    className="flex-[2] bg-navy text-white rounded-full py-3 font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">
                    {busy ? <><Loader2 size={16} className="animate-spin" /> Publishing…</> : 'Publish Plan (live)'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
